<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Receipts</h1>
            <p class="text-muted mb-0">Receipts your team has uploaded for things you bought</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add receipt
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading</span>
          </div>
          <p class="mt-3 text-muted mb-0">Loading receipts…</p>
        </div>

        <!-- Load failed -->
        <div v-else-if="error && !items.length" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="text-danger mb-3" />
          <h2 class="h5">Couldn't load receipts</h2>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- First use -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-file" size="2xl" class="text-muted mb-3" />
          <h2 class="h5">No receipts yet</h2>
          <p class="text-muted mb-3">
            Upload a photo of a receipt and we'll read the vendor, date and amount for you.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add receipt
          </CButton>
        </div>

        <template v-else>
          <CRow class="mb-3">
            <CCol md="6" lg="4">
              <CInput
                v-model="search"
                label="Search"
                placeholder="Company or submitted by"
                class="mb-0"
              >
                <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
              </CInput>
            </CCol>
            <CCol class="d-flex align-items-end justify-content-md-end mt-2 mt-md-0">
              <small class="text-muted">
                {{ filteredItems.length }} of {{ items.length }} receipts
              </small>
            </CCol>
          </CRow>

          <!-- Filtered empty -->
          <div v-if="!filteredItems.length" class="text-center py-4">
            <h2 class="h6">No receipts match your search</h2>
            <CButton color="light" class="mt-2" @click="search = ''">
              <CIcon name="cil-x" /> Clear search
            </CButton>
          </div>

          <template v-else>
            <!-- Desktop table -->
            <div class="d-none d-lg-block">
              <CDataTable
                :items="filteredItems"
                :fields="fields"
                :items-per-page="25"
                :loading="loading"
                hover
                sorter
                pagination
              >
                <template #show_image="{ item }">
                  <td class="py-2">
                    <CImg
                      v-if="item.documentId"
                      thumbnail
                      :src="getImage(item)"
                      height="56"
                      width="56"
                      :alt="`Receipt from ${item.companyName || 'vendor'}`"
                    />
                  </td>
                </template>
                <template #date="{ item }">
                  <td class="py-2">{{ formatDateTime(item.date) }}</td>
                </template>
                <template #companyName="{ item }">
                  <td class="py-2">
                    <span v-if="item.companyName">{{ item.companyName }}</span>
                    <span v-else class="text-muted">Not set</span>
                  </td>
                </template>
                <template #profileName="{ item }">
                  <td class="py-2">{{ item.profileName || "—" }}</td>
                </template>
                <template #totalAmount="{ item }">
                  <td class="py-2 text-right text-nowrap">RM {{ formatMyr(item.totalAmount) }}</td>
                </template>
                <template #actions="{ item }">
                  <td class="py-2 text-right text-nowrap">
                    <CButton
                      color="light"
                      size="sm"
                      class="mr-1"
                      :aria-label="`Edit receipt from ${item.companyName || 'vendor'}`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="light"
                      size="sm"
                      class="text-danger"
                      :aria-label="`Delete receipt from ${item.companyName || 'vendor'}`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </div>

            <!-- Mobile card list -->
            <div class="d-lg-none">
              <CCard v-for="item in filteredItems" :key="item.id" class="mb-2">
                <CCardBody class="p-3">
                  <div class="d-flex justify-content-between">
                    <div>
                      <div class="font-weight-bold">{{ item.companyName || "Receipt" }}</div>
                      <div class="small text-muted">{{ formatDateTime(item.date) }}</div>
                      <div v-if="item.profileName" class="small text-muted">{{ item.profileName }}</div>
                    </div>
                    <div class="font-weight-bold text-nowrap">RM {{ formatMyr(item.totalAmount) }}</div>
                  </div>
                  <div class="d-flex justify-content-between mt-3">
                    <CButton color="light" class="text-danger" @click="showDeleteConfirmation(item)">
                      <CIcon name="cil-trash" /> Delete
                    </CButton>
                    <CButton color="primary" variant="outline" @click="onEdit(item)">
                      <CIcon name="cil-pencil" /> Edit
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </template>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete receipt from ${itemToDelete.companyName || 'this vendor'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The receipt dated {{ formatDate(itemToDelete.date) || "—" }} for
        RM {{ formatMyr(itemToDelete.totalAmount) }} will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <span v-if="deleting" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
          Delete receipt
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ReceiptApi from "@/lib/receiptApi";
import { formatDate, formatDateTime } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

const fields = [
  { key: "show_image", label: "Image", sorter: false, _style: "width:1%" },
  { key: "date", label: "Date" },
  { key: "companyName", label: "Company" },
  { key: "profileName", label: "Submitted by" },
  { key: "totalAmount", label: "Total", _classes: "text-right" },
  { key: "actions", label: "", sorter: false, _style: "width:1%" },
];

export default {
  name: "TenantReceiptList",
  data() {
    return {
      loading: true,
      error: false,
      items: [],
      fields,
      search: "",
      api: new ReceiptApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Vendor Receipts"]);
    this.refreshTable();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => ({
        ...item,
        profileName: this.getProfileName(item),
      }));
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.computedItems;
      return this.computedItems.filter((r) =>
        [r.companyName, r.profileName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  methods: {
    formatDate,
    formatDateTime,
    formatMyr,
    getProfileName(item) {
      return (item.profile && item.profile.fullName) || "";
    },
    getImage(item) {
      return apiUrl + "documents/file/" + item.documentId;
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;
      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result || [];
          self.loading = false;
        })
        .catch((err) => {
          console.error("Load receipts failed", err);
          self.loading = false;
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh receipts. Check your connection and try again.");
          }
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Receipt/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      var self = this;
      self.deleting = true;
      self.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.deleting = false;
          self.warningModal = false;
          self.itemToDelete = {};
          self.$toast.success("Receipt deleted");
          self.refreshTable();
        })
        .catch(({ data } = {}) => {
          self.deleting = false;
          self.$toast.error(
            "Couldn't delete the receipt. " + helper.getErrorMessage(data, "Please try again.")
          );
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/Receipt" });
    },
  },
};
</script>
